import {inject, Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {catchError, EMPTY, filter, map, of, switchMap, take, withLatestFrom} from 'rxjs';
import {Store} from '@ngrx/store';
import {
  initLanguage,
  loadLanguages,
  loadLanguagesError,
  loadLanguagesSuccess,
  setActiveLanguage,
  setActiveLanguageCombined
} from './language.actions';
import {LanguageApiService} from '../services/language-api.service';
import {selectActiveLanguageCode} from './language.selectors';
import {TranslocoService} from '@jsverse/transloco';
import {PreferencesService} from '../../services/preferences.service';
import {Router} from '@angular/router';
import {selectPreferredLanguageCode} from '../../../modules/profile/store/profile.selectors';
import {loadProfileSuccess} from '../../../modules/profile/store/profile.actions';
import {languageCodeToTranslocoMap} from '../enums/language-codes';
import {loadCollection} from '../../../modules/collection/store/collection.actions';
import {userId} from '../../../modules/authentication/store/authentication.selectors';
import {loadNews} from '../../../modules/news/store/news.actions';
import {GlobalState} from '../../../store/store.model';

@Injectable()
export class LanguageEffects {
  private readonly _actions$ = inject(Actions);
  private readonly _languageApiService = inject(LanguageApiService);
  private readonly _store = inject(Store<GlobalState>);
  private readonly _transloco = inject(TranslocoService);
  private readonly _preferences = inject(PreferencesService);
  private readonly _router = inject(Router);

  // Caricamento lingue da API
  private _loadLanguages = createEffect(() => {
    return this._actions$.pipe(
      ofType(loadLanguages),
      switchMap(() => {
        return this._languageApiService.loadLanguages().pipe(
          map((response) => loadLanguagesSuccess({languages: response.data})),
          catchError((error) => of(loadLanguagesError({error})))
        );
      })
    );
  });

  private _setLanguageFromProfile = createEffect(() => {
    return this._actions$.pipe(
      ofType(loadProfileSuccess),
      switchMap(() =>
        this._store.select(selectPreferredLanguageCode).pipe(take(1))
      ),
      filter((langCode): langCode is string => !!langCode),
      map((langCode) => setActiveLanguage({languageCode: langCode}))
    );
  });

  // Inizializzazione lingua al bootstrap
  private _initLanguage = createEffect(() => {
    return this._actions$.pipe(
      ofType(initLanguage),
      switchMap(() =>
        this._store.select(selectActiveLanguageCode).pipe(take(1))
      ),
      filter(lang => !lang),
      map((lang) => {
        let selectedLang!: string;
        const queryLang = this._router.parseUrl(this._router.url).queryParams['lang'];
        if (queryLang) {
          this._router.navigate([], {queryParams: {lang: null}, queryParamsHandling: 'merge'});
          selectedLang = queryLang;
        }
        if (!selectedLang) {
          this._preferences.getLanguage().then(savedLang => {
            selectedLang = savedLang || 'en_GB';
          })
        }
        if (!selectedLang) {
          const browserLang = navigator.language || 'en_GB';
          const formatted = browserLang.replace('-', '_');
          selectedLang = formatted === 'it_IT' ? 'it_IT' : 'en_GB';
        }
        if (!selectedLang) {
          selectedLang = lang || 'en_GB';
        }
        return setActiveLanguage({languageCode: selectedLang});
      })
    );
  });

  private _setLanguageInTransloco$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(setActiveLanguage),
      map(({languageCode}) => {
        const translocoLang = languageCodeToTranslocoMap[languageCode] || 'en_GB';

        this._transloco.setDefaultLang(translocoLang);
        this._transloco.setActiveLang(translocoLang);
        this._preferences.setLanguage(languageCode); // salva full code

        return setActiveLanguageCombined({languageCode});
      })
    );
  });


  // Gestione completa dell'impostazione lingua
  private _handleSetActiveLanguage = createEffect(() => {
    return this._actions$.pipe(
      ofType(setActiveLanguage),
      withLatestFrom(this._store.select(userId)),
      switchMap(([{languageCode}, userId]) => {
        const translocoLang = languageCodeToTranslocoMap[languageCode] || 'en_GB'; // fallback di sicurezza

        this._transloco.setDefaultLang(translocoLang);
        this._transloco.setActiveLang(translocoLang);

        this._preferences.setLanguage(languageCode);


        if (userId) {
          return this._languageApiService.setLanguage(languageCode).pipe(
            switchMap(() => [
              {type: '[Language] Set Active Language Complete'},
              loadCollection({loading: true, reset: true}),
              loadNews()
            ]),
            catchError(() => of({type: '[Language] Set Active Language Failed'}))
          );
        }

        return EMPTY;
      })
    );
  });
}
